import { For, Show, createMemo, createSignal, onCleanup } from 'solid-js'

import { getLocale, useI18n } from '../../i18n'
import {
   PHYSIOLOGY_METRICS,
   getPhysiologyValue,
   type PhysiologyMetric,
   type PhysiologyMetricKey,
} from '../../utils/fastingPhysiology'

const HOUR_IN_MS = 60 * 60 * 1000
const CHART_WIDTH = 280
const CHART_HEIGHT = 32
const CHART_PADDING = 5
const SAMPLE_COUNT = 72

const METRIC_LABEL_KEYS: Record<PhysiologyMetricKey, string> = {
   bloodGlucose: 'Blood glucose',
   insulin: 'Insulin',
   liverGlycogen: 'Liver glycogen',
   ketones: 'Ketones',
}

const METRIC_COLOR_CLASSES: Record<PhysiologyMetricKey, string> = {
   bloodGlucose: 'text-series-glucose',
   insulin: 'text-series-insulin',
   liverGlycogen: 'text-series-glycogen',
   ketones: 'text-series-ketones',
}

const METRIC_SWATCH_CLASSES: Record<PhysiologyMetricKey, string> = {
   bloodGlucose: 'bg-series-glucose',
   insulin: 'bg-series-insulin',
   liverGlycogen: 'bg-series-glycogen',
   ketones: 'bg-series-ketones',
}

const ARROW_KEY_STEP_HOURS: Record<string, number> = {
   ArrowLeft: -0.5,
   ArrowRight: 0.5,
}

// Nutritional ketosis is usually counted from 0.5 mmol/L of beta-hydroxybutyrate.
const KETOSIS_THRESHOLD_MMOL = 0.5

/**
 * Maps a metric value to its y coordinate, clamped to the metric's range.
 *
 * @param metric - The metric whose value range sets the y scale.
 * @param value - The value to place.
 * @returns The y coordinate.
 */
function toChartY(metric: PhysiologyMetric, value: number) {
   const plotHeight = CHART_HEIGHT - CHART_PADDING * 2
   const ratio = (value - metric.min) / (metric.max - metric.min)
   return CHART_PADDING + (1 - Math.min(1, Math.max(0, ratio))) * plotHeight
}

/**
 * Maps a point in the fast to chart coordinates for one metric.
 *
 * @param metric - The metric whose value range sets the y scale.
 * @param hours - Hours since the fast started.
 * @param domainHours - Hours spanned by the x axis.
 * @returns The x and y coordinates.
 */
function toChartPoint(metric: PhysiologyMetric, hours: number, domainHours: number) {
   return {
      x: CHART_PADDING + (hours / domainHours) * (CHART_WIDTH - CHART_PADDING * 2),
      y: toChartY(metric, getPhysiologyValue(metric, hours)),
   }
}

/**
 * Builds an SVG path for a metric between two points in the fast.
 *
 * @param metric - The metric to draw.
 * @param fromHours - Start of the segment.
 * @param toHours - End of the segment.
 * @param domainHours - Hours spanned by the x axis.
 * @returns The path data.
 */
function buildPath(
   metric: PhysiologyMetric,
   fromHours: number,
   toHours: number,
   domainHours: number,
) {
   const steps = Math.max(
      1,
      Math.ceil(((toHours - fromHours) / domainHours) * SAMPLE_COUNT),
   )
   const commands: string[] = []
   for (let step = 0; step <= steps; step += 1) {
      const point = toChartPoint(
         metric,
         fromHours + ((toHours - fromHours) * step) / steps,
         domainHours,
      )
      commands.push(
         `${step === 0 ? 'M' : 'L'}${point.x.toFixed(1)},${point.y.toFixed(1)}`,
      )
   }
   return commands.join(' ')
}

/**
 * Returns a copy of the metric whose y scale also fits every value inside the
 * visible time span, so slow risers (ketones) stay readable on short fasts and
 * grow their scale on long ones.
 *
 * @param metric - The metric to fit.
 * @param domainHours - Hours spanned by the x axis.
 * @returns The metric with its scale ceiling raised as needed.
 */
function fitMetricScale(metric: PhysiologyMetric, domainHours: number) {
   const visibleValues = metric.keypoints
      .filter(([hours]) => hours <= domainHours)
      .map(([, value]) => value)
   const max = Math.max(
      metric.max,
      getPhysiologyValue(metric, domainHours),
      ...visibleValues,
   )
   return { ...metric, max }
}

/**
 * Formats a modeled value with its unit in the active locale.
 *
 * @param metric - The metric, which sets decimals and unit.
 * @param value - The modeled value.
 * @returns The display label.
 */
function formatMetricValue(metric: PhysiologyMetric, value: number) {
   const formatted = new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: metric.decimals,
      maximumFractionDigits: metric.decimals,
   }).format(value)
   return metric.unit === '%' ? `${formatted}%` : `${formatted} ${metric.unit}`
}

/**
 * Draws the inspection crosshair and a hollow marker on the curve.
 *
 * @param props - The marker's chart coordinates.
 * @returns The marker elements.
 */
const InspectionMarker = (props: { x: number; y: number }) => (
   <>
      <line
         x1={props.x}
         x2={props.x}
         y1="0"
         y2={CHART_HEIGHT}
         class="stroke-ink-faint"
         stroke-width="1"
      />
      <circle
         cx={props.x}
         cy={props.y}
         r="3.5"
         class="fill-surface-raised"
         stroke="currentColor"
         stroke-width="2"
      />
   </>
)

/**
 * Renders one metric's label, value, and sparkline (past solid, future dashed).
 * While the timeline is being inspected the value follows the inspected time
 * and a hollow marker shows where it sits on the curve.
 *
 * @param props - The metric, the chart's time domain, and the inspected time.
 * @returns The metric row element.
 */
const MetricRow = (props: {
   metric: PhysiologyMetric
   elapsedHours: number
   domainHours: number
   inspectedHours: number | null
}) => {
   const t = useI18n()
   const label = () => t(METRIC_LABEL_KEYS[props.metric.key])
   /**
    * Returns the metric's modeled value at the inspected time, or now.
    *
    * @returns The formatted value.
    */
   const valueLabel = () =>
      formatMetricValue(
         props.metric,
         getPhysiologyValue(props.metric, props.inspectedHours ?? props.elapsedHours),
      )
   const scaledMetric = createMemo(() => fitMetricScale(props.metric, props.domainHours))
   const nowPoint = () =>
      toChartPoint(scaledMetric(), props.elapsedHours, props.domainHours)
   const thresholdY = () => toChartY(scaledMetric(), KETOSIS_THRESHOLD_MMOL)
   /**
    * Returns where the inspected time sits on this curve, if inspecting.
    *
    * @returns The chart point, or null.
    */
   const inspectedPoint = () =>
      props.inspectedHours === null ?
         null
      :  toChartPoint(scaledMetric(), props.inspectedHours, props.domainHours)

   return (
      <div class={METRIC_COLOR_CLASSES[props.metric.key]}>
         <div class="flex items-baseline justify-between gap-2 text-xs">
            <span class="flex items-center gap-1.5 text-ink-soft">
               <span
                  class={`h-2 w-2 rounded-full ${METRIC_SWATCH_CLASSES[props.metric.key]}`}
                  aria-hidden="true"
               ></span>
               {label()}
            </span>
            <span class="font-mono font-semibold text-ink">{valueLabel()}</span>
         </div>
         <svg
            class="block w-full"
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            role="img"
            aria-label={`${label()}: ${valueLabel()}`}
         >
            <title>{`${label()}: ${valueLabel()}`}</title>
            <Show when={props.metric.key === 'ketones'}>
               <line
                  x1={CHART_PADDING}
                  x2={CHART_WIDTH - CHART_PADDING}
                  y1={thresholdY()}
                  y2={thresholdY()}
                  class="stroke-ink-faint"
                  stroke-width="1"
                  opacity="0.6"
                  stroke-dasharray="2 3"
               />
            </Show>
            <path
               d={buildPath(
                  scaledMetric(),
                  props.elapsedHours,
                  props.domainHours,
                  props.domainHours,
               )}
               fill="none"
               stroke="currentColor"
               stroke-width="1.5"
               stroke-dasharray="3 3"
               stroke-linecap="round"
               opacity="0.4"
            />
            <path
               d={buildPath(scaledMetric(), 0, props.elapsedHours, props.domainHours)}
               fill="none"
               stroke="currentColor"
               stroke-width="2"
               stroke-linecap="round"
               stroke-linejoin="round"
            />
            <circle
               cx={nowPoint().x}
               cy={nowPoint().y}
               r="4"
               fill="currentColor"
               class="stroke-surface-raised"
               stroke-width="2"
            />
            <Show when={inspectedPoint()}>
               {point => <InspectionMarker x={point().x} y={point().y} />}
            </Show>
         </svg>
      </div>
   )
}

/**
 * Tracks which moment of the timeline is being inspected by mouse hover,
 * touch drag, or arrow keys. Touch keeps the reading briefly after lifting.
 *
 * @param domainHours - Hours spanned by the x axis.
 * @param elapsedHours - Hours since the fast started (arrow keys start here).
 * @returns The inspected time and handlers for the plot area.
 */
function createTimelineInspector(domainHours: () => number, elapsedHours: () => number) {
   const [inspectedHours, setInspectedHours] = createSignal<number | null>(null)
   let clearTimerId: number | null = null

   /**
    * Cancels a pending delayed clear.
    */
   const cancelClear = () => {
      if (clearTimerId !== null) {
         window.clearTimeout(clearTimerId)
         clearTimerId = null
      }
   }
   onCleanup(cancelClear)

   /**
    * Inspects the time under the pointer, snapped to quarter hours.
    *
    * @param event - The pointer event over the plot area.
    */
   const inspectAt = (event: PointerEvent) => {
      const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect()
      const chartX = ((event.clientX - bounds.left) / bounds.width) * CHART_WIDTH
      const ratio = (chartX - CHART_PADDING) / (CHART_WIDTH - CHART_PADDING * 2)
      const hours = Math.min(1, Math.max(0, ratio)) * domainHours()
      cancelClear()
      setInspectedHours(Math.round(hours * 4) / 4)
   }

   return {
      inspectedHours,
      handlers: {
         onPointerDown: inspectAt,
         onPointerMove: inspectAt,
         /**
          * Clears a mouse hover at once; a touch reading lingers so it can be read.
          *
          * @param event - The pointer event.
          */
         onPointerLeave: (event: PointerEvent) => {
            cancelClear()
            if (event.pointerType === 'mouse') {
               setInspectedHours(null)
               return
            }
            clearTimerId = window.setTimeout(() => setInspectedHours(null), 2500)
         },
         /**
          * Steps the inspected time by half an hour with the arrow keys.
          *
          * @param event - The keyboard event.
          */
         onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
               setInspectedHours(null)
               return
            }
            const step = ARROW_KEY_STEP_HOURS[event.key] ?? 0
            if (step === 0) {
               return
            }
            event.preventDefault()
            const start = inspectedHours() ?? Math.round(elapsedHours() * 2) / 2
            setInspectedHours(Math.min(domainHours(), Math.max(0, start + step)))
         },
         onBlur: () => setInspectedHours(null),
      },
   }
}

/**
 * Shows modeled blood glucose, insulin, liver glycogen, and ketone curves for the
 * current fast as small multiples, each on its own scale, with "now" marked.
 *
 * @param props - Elapsed and target fast duration in milliseconds, plus extra classes.
 * @returns The chart card element.
 */
const MetabolicStateChart = (props: {
   elapsedMs: number
   targetMs: number
   class?: string
}) => {
   const t = useI18n()
   const elapsedHours = () => props.elapsedMs / HOUR_IN_MS
   const domainHours = () =>
      Math.max(1, Math.ceil(Math.max(props.targetMs / HOUR_IN_MS, elapsedHours())))
   const inspector = createTimelineInspector(domainHours, elapsedHours)
   /**
    * Returns the inspected time as hours and minutes, or nothing when idle.
    *
    * @returns The time label.
    */
   const inspectedLabel = () => {
      const hours = inspector.inspectedHours()
      if (hours === null) {
         return ''
      }
      const totalMinutes = Math.round(hours * 60)
      return t('{hours}h {minutes}m', {
         hours: Math.floor(totalMinutes / 60),
         minutes: totalMinutes % 60,
      })
   }

   return (
      <section
         id="metabolic-state-chart"
         class={`w-full max-w-xl lg:w-72 rounded-lg border border-edge-soft bg-surface-raised p-3 shadow-sm lg:border-0 lg:bg-transparent lg:px-0 lg:shadow-none ${props.class ?? ''}`}
         aria-describedby="metabolic-state-disclaimer"
      >
         <div
            class="cursor-crosshair touch-pan-y space-y-2 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-strong"
            tabindex="0"
            aria-label={t('Use arrow keys to inspect the timeline')}
            {...inspector.handlers}
         >
            <For each={PHYSIOLOGY_METRICS}>
               {metric => (
                  <MetricRow
                     metric={metric}
                     elapsedHours={elapsedHours()}
                     domainHours={domainHours()}
                     inspectedHours={inspector.inspectedHours()}
                  />
               )}
            </For>
         </div>
         <div class="mt-1 flex justify-between px-1 text-[10px] text-ink-faint">
            <span>0h</span>
            <span class="font-semibold text-ink-soft" aria-live="polite">
               {inspectedLabel()}
            </span>
            <span>{domainHours()}h</span>
         </div>
         <p id="metabolic-state-disclaimer" class="mt-2 text-xs text-ink-faint">
            {t('Modeled for a healthy adult, not measured')}
         </p>
      </section>
   )
}

export default MetabolicStateChart
